// @flow

import React, { PureComponent } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import styled, { createGlobalStyle } from 'styled-components';
import { ContextMenuTrigger, ContextMenu, MenuItem } from 'react-contextmenu';
import relayEnvironment from 'shared/gql/relayEnvironment';
import SortableTree, {
  toggleExpandedForAll,
  getVisibleNodeCount,
} from 'react-sortable-tree';
import ReactTooltip from 'react-tooltip';
import isEqual from 'lodash.isequal';
import { createFragmentContainer, graphql, fetchQuery } from 'react-relay';
import { i18n, createTooltip, Analytics, getCurrencySymbol } from 'shared/utils';
import { Link } from 'react-router-dom';
import * as Actions from 'main-app/store/Actions';
import Icon from 'shared/components/common/Icon';
import Button from 'shared/components/common/Button';
import {
  Branch,
  Header,
  Column,
  ItemNumber,
  ButtonWrapper,
  TooltipTitle,
  TooltipContent,
  BomTypeBadge,
  PhantomTag,
  DetailModalOverlay,
  DetailModalCard,
  DetailModalHeader,
  DetailModalTitle,
  DetailModalCloseButton,
  DetailModalBody,
  DetailSection,
  DetailSectionTitle,
  DetailRow,
  DetailLabel,
  DetailValue,
} from 'shared/components/common/BOMTree';
import EmptyListResults from 'shared/components/common/EmptyListResults';
import DeleteItemChildMutation from 'main-app/mutations/DeleteItemChild';
import AddUpdateItemChildModal from './AddUpdateItemChildModal';
import BomFishbowlPrint from './BomFishbowlPrint';
import { BOM_PRINT_REPORT_OPTIONS } from './bomPrintReports';
import type { BomPrintReportType } from './bomPrintReports';
import type { ItemSubComponentsTree_itemChildEdges as ItemChildEdgesFragment } from './__generated__/ItemSubComponentsTree_itemChildEdges';

const BomPrintGlobalStyle = createGlobalStyle`
  @media print {
    @page {
      size: portrait;
      margin: 0.5in;
    }

    body.printing-bom > *:not(.bom-print-root) {
      display: none !important;
    }

    body.printing-bom .bom-print-root {
      display: block !important;
      position: static !important;
      width: 100% !important;
      padding: 0 !important;
      margin: 0 !important;
      background: #fff !important;
    }
  }
`;

const BomPrintRoot = styled.div`
  display: none;
`;

type Props = {
  itemChildEdges: ItemChildEdgesFragment,
  parentItemId: string,
  parentItemName: string,
  parentItemNumber: string,
  parentBomNumber: string,
  parentItemDescription: string,
  parentItemUOM: string,
  companyName: string,
};

type State = {
  addModalOpen: boolean,
  childItem: ?Object,
  treeData: ?Array<Object>,
  toggle: ?boolean,
  selectedBomType: string,
  detailModalOpen: boolean,
  detailItem: ?Object,
};

const ItemSubComponentsTreeQuery = graphql`
  query ItemSubComponentsTreeQuery($id: ID!) {
    item(id: $id) {
      id
      name
      itemNumber
      description
      partNumber
      upc
      cost
      totalCalculatedCost
      quantityUOM {
        id
        symbol
      }
      category {
        id
        name
      }
      childItems(first: null) @connection(key: "Item_childItems", filters: []) {
        edges {
          usageQty
          bomType
          referenceDesignator
          findNumber
          notes
          isPhantom
          scrapFactor
          leadTimeOffsetDays
          isBackflush
          workflowStateId
          fixedQuantity
          isOptional
          selectionGroup
          isDefaultSelection
          optionLabel
          minQuantity
          maxQuantity
          priceAdjustment
          isCustomerVisible
          salesDisplayName
          componentPrice
          discountPercent
          sortOrder
          isShippedSeparately
          isReplaceable
          serviceIntervalDays
          meanTimeBetweenFailure
          repairMethod
          serviceNotes
          isCriticalSpare
          node {
            id
            itemNumber
            name
            description
            partNumber
            upc
            cost
            totalCalculatedCost
            quantityUOM {
              id
              symbol
            }
            category {
              id
              name
            }
          }
        }
      }
    }
  }
`;

class ItemSubComponentsTree extends PureComponent<Props, State> {
  menuTriggerRefs = {};
  state = {
    addModalOpen: false,
    childItem: null,
    treeData: [],
    toggle: true,
    selectedBomType: 'all',
    selectedPrintReport: 'single-level',
    detailModalOpen: false,
    detailItem: null,
  };

  componentDidMount() {
    this.formatTreeData();
    window.addEventListener('afterprint', this.handleAfterPrint);
  }

  componentWillUnmount() {
    window.removeEventListener('afterprint', this.handleAfterPrint);
    document.body.classList.remove('printing-bom');
  }

  componentDidUpdate(prevProps, prevState) {
    const { itemChildEdges } = this.props;
    const { selectedBomType } = this.state;
    const isSame = isEqual(prevProps.itemChildEdges, itemChildEdges);
    const bomTypeChanged = prevState.selectedBomType !== selectedBomType;

    if ((prevProps.itemChildEdges && itemChildEdges && !isSame) || bomTypeChanged) {
      this.formatTreeData();
    }
  }

  formatTreeData = async () => {
    const { itemChildEdges } = this.props;
    const { selectedBomType } = this.state;

    // Filter edges by BOM type
    const filteredEdges =
      selectedBomType === 'all'
        ? itemChildEdges
        : itemChildEdges.filter(edge => edge.bomType === selectedBomType);

    const componentTreeData = await this.getBranches([...filteredEdges], true);
    const treeData = [...componentTreeData];

    this.setState({
      treeData,
      toggle: true,
    });
  };

  getBranchContent = (item: Object, root: ?boolean = false) => {
    if (!item) return null;

    const {
      id,
      itemNumber,
      name,
      quantityUOM,
      usageQty,
      bomType,
      referenceDesignator,
      isPhantom,
      optionLabel,
      isOptional,
      selectionGroup,
      salesDisplayName,
      isCustomerVisible,
      isCriticalSpare,
    } = item;

    return (
      <Branch
        onClick={() => this.handleOpenDetailModal(item)}
        style={{ cursor: 'pointer' }}
      >
        <ItemNumber>
          <Link
            to={`/item/${id}`}
            onClick={e => e.stopPropagation()}
          >
            {itemNumber}
          </Link>
          {isPhantom && <PhantomTag>(Phantom)</PhantomTag>}
          {isOptional && <PhantomTag>(Optional)</PhantomTag>}
          {isCustomerVisible === false && <PhantomTag>(Hidden)</PhantomTag>}
          {isCriticalSpare && <PhantomTag style={{ color: '#c62828' }}>(Critical)</PhantomTag>}
        </ItemNumber>
        <Column>
          <Header>{i18n.t('Item Component')}</Header>
          {salesDisplayName || optionLabel || i18n.t(name || 'No Name')}
        </Column>

        {referenceDesignator && (
          <Column>
            <Header>{i18n.t('Ref Des')}</Header>
            {referenceDesignator}
          </Column>
        )}

        {selectionGroup && (
          <Column>
            <Header>{i18n.t('Option Group')}</Header>
            {selectionGroup}
          </Column>
        )}

        <Column>
          <Header>{i18n.t('Est. Usage')}</Header>
          {i18n.t(`${usageQty.toLocaleString()} ${quantityUOM?.symbol || ''}`)}
        </Column>

        <Column>
          <Header>{i18n.t('BOM Type')}</Header>
          <BomTypeBadge type={bomType || 'manufacturing'}>
            {bomType || 'manufacturing'}
          </BomTypeBadge>
        </Column>
      </Branch>
    );
  };

  getBranches = async (data: Array<Object>, root: ?boolean = false) => {
    if (!data) return [];
    const branches = await Promise.all(
      data.map(async branch => {
        const node = branch.node.item || branch.node;
        const item = this.mergeEdgeIntoItem(node, branch);

        const content = this.getBranchContent(item, root);
        const record = {
          itemId: item.id,
          title: content,
          children: [],
          tooltip: item,
        };

        // if the item doesn't have childItems as a property, fetch more details with the item Id, and use the resulting item in the next map
        const fetchedItem = !item.childItems
          ? await this.asyncFetchItem(item.id)
          : item;

        const normalizedItem = fetchedItem.item || fetchedItem;
        const updatedItem = this.mergeEdgeIntoItem(normalizedItem, item);

        if (updatedItem?.childItems.edges.length) {
          const children = await this.getNestedChildren(updatedItem);

          return {
            ...record,
            children,
          };
        }
        return record;
      }),
    );

    return branches;
  };

  mergeEdgeIntoItem = (node: Object, edge: Object): Object => {
    const item = { ...node };
    item.usageQty = edge.usageQty || 0;
    item.bomType = edge.bomType || 'manufacturing';
    item.referenceDesignator = edge.referenceDesignator || null;
    item.findNumber = edge.findNumber || null;
    item.notes = edge.notes || null;
    item.isPhantom = edge.isPhantom || false;
    item.scrapFactor = edge.scrapFactor || 0;
    item.leadTimeOffsetDays = edge.leadTimeOffsetDays || 0;
    item.isBackflush = edge.isBackflush || false;
    item.workflowStateId = edge.workflowStateId || null;
    item.fixedQuantity = edge.fixedQuantity || 0;
    item.isOptional = edge.isOptional || false;
    item.selectionGroup = edge.selectionGroup || null;
    item.isDefaultSelection = edge.isDefaultSelection || false;
    item.optionLabel = edge.optionLabel || null;
    item.minQuantity = edge.minQuantity || 0;
    item.maxQuantity = edge.maxQuantity != null ? edge.maxQuantity : null;
    item.priceAdjustment = edge.priceAdjustment || 0;
    item.isCustomerVisible =
      edge.isCustomerVisible !== undefined ? edge.isCustomerVisible : true;
    item.salesDisplayName = edge.salesDisplayName || null;
    item.componentPrice = edge.componentPrice || 0;
    item.discountPercent = edge.discountPercent || 0;
    item.sortOrder = edge.sortOrder || 0;
    item.isShippedSeparately = edge.isShippedSeparately || false;
    item.isReplaceable =
      edge.isReplaceable !== undefined ? edge.isReplaceable : true;
    item.serviceIntervalDays = edge.serviceIntervalDays || null;
    item.meanTimeBetweenFailure = edge.meanTimeBetweenFailure || null;
    item.repairMethod = edge.repairMethod || null;
    item.serviceNotes = edge.serviceNotes || null;
    item.isCriticalSpare = edge.isCriticalSpare || false;
    return item;
  };

  getNestedChildren = async (updatedItem: Object) => {
    const children = await Promise.all(
      updatedItem.childItems.edges.map(async edge => {
        const { item } = await this.asyncFetchItem(edge.node.id);
        const fetchedItem = this.mergeEdgeIntoItem(item, edge);

        const fetchedItemContent = this.getBranchContent(fetchedItem);

        const fetchedItemChildren = await this.getBranches(
          fetchedItem.childItems.edges,
          false,
        );
        return {
          itemId: fetchedItem.id,
          title: fetchedItemContent,
          children: fetchedItemChildren,
          tooltip: fetchedItem,
        };
      }),
    );

    return children;
  };

  asyncFetchItem = async (itemId: string) => {
    return await fetchQuery(relayEnvironment, ItemSubComponentsTreeQuery, {
      id: itemId,
    });
  };

  getItemById = (itemId: string) => {
    const { itemChildEdges } = this.props;

    const item = itemChildEdges.find(edge => {
      return edge?.node?.id === itemId;
    });

    return (
      {
        usageQty: item?.usageQty,
        bomType: item?.bomType,
        referenceDesignator: item?.referenceDesignator,
        findNumber: item?.findNumber,
        notes: item?.notes,
        isPhantom: item?.isPhantom,
        scrapFactor: item?.scrapFactor,
        leadTimeOffsetDays: item?.leadTimeOffsetDays,
        isBackflush: item?.isBackflush,
        workflowStateId: item?.workflowStateId,
        fixedQuantity: item?.fixedQuantity,
        isOptional: item?.isOptional,
        selectionGroup: item?.selectionGroup,
        isDefaultSelection: item?.isDefaultSelection,
        optionLabel: item?.optionLabel,
        minQuantity: item?.minQuantity,
        maxQuantity: item?.maxQuantity,
        priceAdjustment: item?.priceAdjustment,
        isCustomerVisible: item?.isCustomerVisible,
        salesDisplayName: item?.salesDisplayName,
        componentPrice: item?.componentPrice,
        discountPercent: item?.discountPercent,
        sortOrder: item?.sortOrder,
        isShippedSeparately: item?.isShippedSeparately,
        isReplaceable: item?.isReplaceable,
        serviceIntervalDays: item?.serviceIntervalDays,
        meanTimeBetweenFailure: item?.meanTimeBetweenFailure,
        repairMethod: item?.repairMethod,
        serviceNotes: item?.serviceNotes,
        isCriticalSpare: item?.isCriticalSpare,
        ...item?.node,
      } || null
    );
  };

  handleOpenAddUpdateModal = (e, data, target) => {
    if (!data) {
      this.setState({
        childItem: null,
        addModalOpen: true,
      });
      return;
    }

    const itemId = target.getAttribute('item-id');
    const childItem = this.getItemById(itemId);

    this.setState({
      childItem,
      addModalOpen: true,
    });
  };

  handleCloseAllModals = () => {
    this.setState({
      childItem: null,
      addModalOpen: false,
    });
  };

  handleOpenDetailModal = (item: Object) => {
    this.setState({
      detailItem: item,
      detailModalOpen: true,
    });
  };

  handleCloseDetailModal = () => {
    this.setState({
      detailItem: null,
      detailModalOpen: false,
    });
  };

  handleAfterPrint = () => {
    document.body.classList.remove('printing-bom');
  };

  handlePrintBom = () => {
    document.body.classList.add('printing-bom');
    window.print();
  };

  renderBomPrintTable = () => {
    const { treeData, selectedBomType, selectedPrintReport } = this.state;
    const {
      parentItemName,
      parentItemNumber,
      parentBomNumber,
      parentItemDescription,
      parentItemUOM,
      companyName,
    } = this.props;

    const bomTypeLabel =
      {
        all: i18n.t('Standard'),
        engineering: i18n.t('Engineering'),
        manufacturing: i18n.t('Standard'),
        configurable: i18n.t('Configurable'),
        sales: i18n.t('Sales'),
        service: i18n.t('Service'),
      }[selectedBomType] || i18n.t('Standard');

    return (
      <BomFishbowlPrint
        reportType={selectedPrintReport}
        treeData={treeData}
        parent={{
          itemNumber: parentItemNumber,
          bomNumber: parentBomNumber,
          name: parentItemName,
          description: parentItemDescription,
          uom: (parentItemUOM || '').toUpperCase(),
          bomTypeLabel,
          companyName,
        }}
      />
    );
  };

  renderDetailModal = () => {
    const { detailItem, detailModalOpen } = this.state;

    if (!detailModalOpen || !detailItem) return null;

    const isEngineering = detailItem.bomType === 'engineering';
    const isManufacturing = detailItem.bomType === 'manufacturing' || !detailItem.bomType;
    const isConfigurable = detailItem.bomType === 'configurable';
    const isSales = detailItem.bomType === 'sales';
    const isService = detailItem.bomType === 'service';

    return (
      <DetailModalOverlay onClick={this.handleCloseDetailModal}>
        <DetailModalCard onClick={e => e.stopPropagation()}>
          <DetailModalHeader>
            <DetailModalTitle>
              {i18n.t('Component Details')}
            </DetailModalTitle>
            <DetailModalCloseButton onClick={this.handleCloseDetailModal}>
              ×
            </DetailModalCloseButton>
          </DetailModalHeader>
          <DetailModalBody>
            <DetailSection>
              <DetailSectionTitle>{i18n.t('General Information')}</DetailSectionTitle>
              <DetailRow>
                <DetailLabel>{i18n.t('Item Number')}</DetailLabel>
                <DetailValue>{detailItem.itemNumber || '-'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>{i18n.t('Item Name')}</DetailLabel>
                <DetailValue>{detailItem.name || '-'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>{i18n.t('Part Number')}</DetailLabel>
                <DetailValue>{detailItem.partNumber || '-'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>{i18n.t('UPC')}</DetailLabel>
                <DetailValue>{detailItem.upc || '-'}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>{i18n.t('Category')}</DetailLabel>
                <DetailValue>{detailItem.category?.name || '-'}</DetailValue>
              </DetailRow>
            </DetailSection>

            <DetailSection>
              <DetailSectionTitle>{i18n.t('BOM Configuration')}</DetailSectionTitle>
              <DetailRow>
                <DetailLabel>{i18n.t('BOM Type')}</DetailLabel>
                <DetailValue>
                  <BomTypeBadge type={detailItem.bomType || 'manufacturing'}>
                    {detailItem.bomType || 'manufacturing'}
                  </BomTypeBadge>
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>{i18n.t('Estimated Usage')}</DetailLabel>
                <DetailValue>
                  {detailItem.usageQty?.toLocaleString() || '0'}{' '}
                  {detailItem.quantityUOM?.symbol || ''}
                </DetailValue>
              </DetailRow>
            </DetailSection>

            {isEngineering && (
              <DetailSection>
                <DetailSectionTitle>{i18n.t('Engineering Details')}</DetailSectionTitle>
                <DetailRow>
                  <DetailLabel>{i18n.t('Reference Designator')}</DetailLabel>
                  <DetailValue>{detailItem.referenceDesignator || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Find Number')}</DetailLabel>
                  <DetailValue>{detailItem.findNumber || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Phantom Assembly')}</DetailLabel>
                  <DetailValue>
                    {detailItem.isPhantom ? (
                      <PhantomTag style={{ marginLeft: 0 }}>{i18n.t('Yes - Phantom')}</PhantomTag>
                    ) : (
                      i18n.t('No')
                    )}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Notes')}</DetailLabel>
                  <DetailValue style={{ whiteSpace: 'pre-wrap' }}>
                    {detailItem.notes || '-'}
                  </DetailValue>
                </DetailRow>
              </DetailSection>
            )}

            {isManufacturing && (
              <DetailSection>
                <DetailSectionTitle>{i18n.t('Manufacturing Details')}</DetailSectionTitle>
                <DetailRow>
                  <DetailLabel>{i18n.t('Scrap Factor')}</DetailLabel>
                  <DetailValue>
                    {detailItem.scrapFactor ? `${detailItem.scrapFactor}%` : '0%'}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Lead Time Offset')}</DetailLabel>
                  <DetailValue>
                    {detailItem.leadTimeOffsetDays
                      ? `${detailItem.leadTimeOffsetDays} day${detailItem.leadTimeOffsetDays !== 1 ? 's' : ''}`
                      : i18n.t('None')}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Backflush')}</DetailLabel>
                  <DetailValue>
                    {detailItem.isBackflush ? i18n.t('Yes - Auto-deduct') : i18n.t('No')}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Fixed Quantity')}</DetailLabel>
                  <DetailValue>
                    {detailItem.fixedQuantity
                      ? `${detailItem.fixedQuantity.toLocaleString()} ${detailItem.quantityUOM?.symbol || ''}`
                      : i18n.t('None')}
                  </DetailValue>
                </DetailRow>
              </DetailSection>
            )}

            {isConfigurable && (
              <DetailSection>
                <DetailSectionTitle>{i18n.t('Configuration Details')}</DetailSectionTitle>
                <DetailRow>
                  <DetailLabel>{i18n.t('Option Label')}</DetailLabel>
                  <DetailValue>{detailItem.optionLabel || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Optional')}</DetailLabel>
                  <DetailValue>
                    {detailItem.isOptional ? i18n.t('Yes') : i18n.t('No - Required')}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Selection Group')}</DetailLabel>
                  <DetailValue>{detailItem.selectionGroup || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Default Selection')}</DetailLabel>
                  <DetailValue>
                    {detailItem.isDefaultSelection ? i18n.t('Yes') : i18n.t('No')}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Quantity Range')}</DetailLabel>
                  <DetailValue>
                    {detailItem.minQuantity || 0}
                    {' - '}
                    {detailItem.maxQuantity != null
                      ? detailItem.maxQuantity
                      : i18n.t('Unlimited')}
                    {' '}
                    {detailItem.quantityUOM?.symbol || ''}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Price Adjustment')}</DetailLabel>
                  <DetailValue>
                    {detailItem.priceAdjustment
                      ? `${detailItem.priceAdjustment > 0 ? '+' : ''}${getCurrencySymbol()}${detailItem.priceAdjustment.toLocaleString()}`
                      : i18n.t('None')}
                  </DetailValue>
                </DetailRow>
              </DetailSection>
            )}

            {isSales && (
              <DetailSection>
                <DetailSectionTitle>{i18n.t('Sales Details')}</DetailSectionTitle>
                <DetailRow>
                  <DetailLabel>{i18n.t('Display Name')}</DetailLabel>
                  <DetailValue>{detailItem.salesDisplayName || i18n.t('(Using item name)')}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Customer Visible')}</DetailLabel>
                  <DetailValue>
                    {detailItem.isCustomerVisible !== false ? i18n.t('Yes') : i18n.t('No - Internal Only')}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Component Price')}</DetailLabel>
                  <DetailValue>
                    {detailItem.componentPrice
                      ? `${getCurrencySymbol()}${detailItem.componentPrice.toLocaleString()}`
                      : `${getCurrencySymbol()}0`}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Bundle Discount')}</DetailLabel>
                  <DetailValue>
                    {detailItem.discountPercent
                      ? `${detailItem.discountPercent}%`
                      : i18n.t('None')}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Sort Order')}</DetailLabel>
                  <DetailValue>{detailItem.sortOrder || 0}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Ships Separately')}</DetailLabel>
                  <DetailValue>
                    {detailItem.isShippedSeparately ? i18n.t('Yes') : i18n.t('No')}
                  </DetailValue>
                </DetailRow>
              </DetailSection>
            )}

            {isService && (
              <DetailSection>
                <DetailSectionTitle>{i18n.t('Service Details')}</DetailSectionTitle>
                <DetailRow>
                  <DetailLabel>{i18n.t('Replaceable')}</DetailLabel>
                  <DetailValue>
                    {detailItem.isReplaceable !== false ? i18n.t('Yes - Field Replaceable') : i18n.t('No')}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Critical Spare')}</DetailLabel>
                  <DetailValue>
                    {detailItem.isCriticalSpare
                      ? i18n.t('Yes - Must Stock')
                      : i18n.t('No')}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Repair Method')}</DetailLabel>
                  <DetailValue>{detailItem.repairMethod || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Service Interval')}</DetailLabel>
                  <DetailValue>
                    {detailItem.serviceIntervalDays
                      ? `${detailItem.serviceIntervalDays} day${detailItem.serviceIntervalDays !== 1 ? 's' : ''}`
                      : i18n.t('Not Set')}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('MTBF')}</DetailLabel>
                  <DetailValue>
                    {detailItem.meanTimeBetweenFailure
                      ? `${detailItem.meanTimeBetweenFailure.toLocaleString()} hours`
                      : i18n.t('Not Set')}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>{i18n.t('Service Notes')}</DetailLabel>
                  <DetailValue style={{ whiteSpace: 'pre-wrap' }}>
                    {detailItem.serviceNotes || '-'}
                  </DetailValue>
                </DetailRow>
              </DetailSection>
            )}
          </DetailModalBody>
        </DetailModalCard>
      </DetailModalOverlay>
    );
  };

  generateChildrenTooltips = (data: Array<Object>, result = []) => {
    const tooltips = data.flatMap(node => {
      const isExistingTooltip = result.some(
        record => record.props.id === node.itemId,
      );
      if (!isExistingTooltip) {
        const content = (
          <div>
            <TooltipTitle>{i18n.t('Item Name')}</TooltipTitle>
            <TooltipContent>{node.tooltip.name || ''}</TooltipContent>
            <TooltipTitle>{i18n.t('Part #')}</TooltipTitle>
            <TooltipContent>{node.tooltip.partNumber || ''}</TooltipContent>
            <TooltipTitle>{i18n.t('UPC')}</TooltipTitle>
            <TooltipContent>{node.tooltip.upc || ''}</TooltipContent>
            <TooltipTitle>{i18n.t('Category')}</TooltipTitle>
            <TooltipContent>
              {node.tooltip?.category?.name || ''}
            </TooltipContent>
          </div>
        );

        const tooltip = createTooltip({ id: node.itemId, content });
        result.push(tooltip);
      }
      if (node.children.length) {
        return this.generateChildrenTooltips(node.children, result);
      }
      return result;
    });

    return Array.from(new Set(tooltips));
  };

  getAllTooltips = (treeData: ?Array<Object>) =>
    !treeData || !treeData.length
      ? null
      : this.generateChildrenTooltips(treeData);

  toggleNodeExpansion = expanded => {
    this.setState(prevState => ({
      treeData: toggleExpandedForAll({
        treeData: prevState.treeData,
        expanded,
      }),
      toggle: !expanded,
    }));
  };

  handleUnlink = async (e, data, target) => {
    const { parentItemId } = this.props;
    const itemId = target.getAttribute('item-id');

    try {
      await DeleteItemChildMutation.commit({
        variables: {
          input: {
            parentItemId,
            childItemId: itemId,
          },
        },
      });

      Analytics.trackEvent('Unlink Component');
      Actions.alertNotification(
        i18n.t('Item Successfully Unlinked'),
        'success',
      );
    } catch (e) {
      Actions.alertNotification(e.message, 'error');
    }
  };

  render() {
    const { itemChildEdges, parentItemId, parentBomNumber, parentItemNumber } = this.props;
    const { childItem, addModalOpen, treeData, toggle, selectedBomType, selectedPrintReport } = this.state;
    const tooltips = this.getAllTooltips(treeData);
    const hasComponents = treeData && treeData.length > 0;

    const bomTypeOptions = [
      { value: 'all', label: i18n.t('All BOMs') },
      { value: 'engineering', label: i18n.t('Engineering BOM') },
      { value: 'manufacturing', label: i18n.t('Manufacturing BOM') },
      { value: 'configurable', label: i18n.t('Configurable BOM') },
      { value: 'sales', label: i18n.t('Sales BOM') },
      { value: 'service', label: i18n.t('Service BOM') },
    ];

    return (
      <>
        <BomPrintGlobalStyle />
        {createPortal(
          <BomPrintRoot className="bom-print-root">
            {hasComponents && this.renderBomPrintTable()}
          </BomPrintRoot>,
          document.body,
        )}
        <div
          style={{
            paddingBottom: 16,
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {parentBomNumber ? (
            <div style={{ fontSize: '14px' }}>
              <span style={{ fontWeight: 600 }}>{i18n.t('BOM #')}:</span>{' '}
              {parentBomNumber}
              <span style={{ color: '#666', marginLeft: '12px' }}>
                ({i18n.t('Inventory #')} {this.props.parentItemNumber})
              </span>
            </div>
          ) : null}
        </div>
        <div
          style={{
            paddingBottom: 16,
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 500, fontSize: '14px' }}>
              {i18n.t('View:')}
            </span>
            <select
              value={selectedBomType}
              onChange={e =>
                this.setState({ selectedBomType: e.target.value })
              }
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '14px',
                minWidth: '180px',
                cursor: 'pointer',
              }}
            >
              {bomTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 500, fontSize: '14px' }}>
              {i18n.t('Print:')}
            </span>
            <select
              value={selectedPrintReport}
              onChange={e =>
                this.setState({
                  selectedPrintReport: ((e.target.value: any): BomPrintReportType),
                })
              }
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '14px',
                minWidth: '180px',
                cursor: 'pointer',
              }}
            >
              {BOM_PRINT_REPORT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {i18n.t(option.label)}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={this.handleOpenAddUpdateModal} width="auto">
            {i18n.t('Add Components')}
          </Button>
          <Button
            theme="border"
            width="auto"
            disabled={!hasComponents}
            onClick={this.handlePrintBom}
          >
            {i18n.t('Print BOM')}
          </Button>
        </div>
        {!treeData || !treeData.length ? (
          <EmptyListResults
            graphic="items"
            message={i18n.t(
              'Components associated to inventory can be created and managed here. Operators will have access to component information.',
            )}
          />
        ) : (
          <>
            <Button
              theme="border-white"
              width="auto"
              onClick={() => this.toggleNodeExpansion(toggle)}
            >
              {i18n.t(`${toggle ? 'Expand' : 'Collapse'} all`)}
            </Button>
            <SortableTree
              treeData={treeData}
              onChange={treeData => {
                const treeDataLen = treeData.length;
                const visibleNodeCount = getVisibleNodeCount({ treeData });

                this.setState({
                  treeData,
                  toggle: treeDataLen === visibleNodeCount ? true : false,
                });
              }}
              canDrag={false}
              style={{ minHeight: '750px' }}
              rowHeight={74}
              generateNodeProps={row => {
                const tooltipIcon = (
                  <div
                    data-for={row.node.itemId}
                    data-tip
                    key={row.node.itemId}
                  >
                    <Icon
                      type="circle-question"
                      size={16}
                      style={{ marginRight: '24px', marginTop: '4px' }}
                    />
                  </div>
                );

                return {
                  buttons: !row.parentNode
                    ? [
                        <ButtonWrapper>
                          {tooltipIcon}
                          <ContextMenuTrigger
                            ref={r =>
                              (this.menuTriggerRefs[`${row.node.itemId}`] = r)
                            }
                            id="components-menu"
                            attributes={{
                              'item-id': row.node.itemId,
                            }}
                          >
                            <Icon
                              type="circle-context-menu"
                              size={24}
                              onClick={(e, data, target) => {
                                if (
                                  this.menuTriggerRefs[`${row.node.itemId}`]
                                ) {
                                  this.menuTriggerRefs[
                                    `${row.node.itemId}`
                                  ].handleContextClick(e, data, target);
                                }
                              }}
                            />
                          </ContextMenuTrigger>
                        </ButtonWrapper>,
                      ]
                    : [tooltipIcon],
                };
              }}
              reactVirtualizedListProps={{
                onRowsRendered: () => ReactTooltip.rebuild(),
              }}
            />
            <ContextMenu id="components-menu">
              <MenuItem onClick={this.handleOpenAddUpdateModal}>
                {i18n.t('Edit Estimated Usage')}
              </MenuItem>
              <MenuItem onClick={this.handleUnlink}>
                {i18n.t('Unlink Item')}
              </MenuItem>
            </ContextMenu>

            {tooltips}
          </>
        )}
        {addModalOpen && (
          <AddUpdateItemChildModal
            onClose={this.handleCloseAllModals}
            parentItemId={parentItemId}
            childItem={childItem}
            excludeIds={itemChildEdges.map(edge => edge?.node.id)}
          />
        )}
        {this.renderDetailModal()}
      </>
    );
  }
}

export default createFragmentContainer(ItemSubComponentsTree, {
  itemChildEdges: graphql`
    fragment ItemSubComponentsTree_itemChildEdges on ItemChildEdge
      @relay(plural: true) {
      usageQty
      bomType
      referenceDesignator
      findNumber
      notes
      isPhantom
      scrapFactor
      leadTimeOffsetDays
      isBackflush
      workflowStateId
      fixedQuantity
      isOptional
      selectionGroup
      isDefaultSelection
      optionLabel
      minQuantity
      maxQuantity
      priceAdjustment
      isCustomerVisible
      salesDisplayName
      componentPrice
      discountPercent
      sortOrder
      isShippedSeparately
      isReplaceable
      serviceIntervalDays
      meanTimeBetweenFailure
      repairMethod
      serviceNotes
      isCriticalSpare
      node {
        id
        itemNumber
        name
        description
        partNumber
        upc
        cost
        totalCalculatedCost
        quantityUOM {
          id
          symbol
        }
        category {
          id
          name
        }
      }
    }
  `,
});
